import { LogoutOutlined, PushpinOutlined } from "@ant-design/icons";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { Menu, Spin } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useUser } from "hooks/useUser.ts";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { PaldockRoute, RouteType } from "utils/createPaldockFileRoute.ts";
import { isRouteAccessible } from "utils/isRouteAccessible.ts";
import { removeTenantSlugParam } from "utils/removeTenantSlugParam.ts";

type MenuItem = {
    children?: MenuItem[];
} & ItemType;

export const DynamicSideNavigation = ({ toggleMenuCollapse }: { toggleMenuCollapse: () => void }) => {
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const { t: tC } = usePaldockTranslation("common");
    const { t: tRaw } = usePaldockTranslation();
    const userUtils = useUser();
    const routerUtils = useRouter();
    const routerState = useRouterState();
    const permissionUtils = usePermission();

    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
    const currentPath = routerState.location.pathname;

    const pathSegments = useMemo(() => {
        if (!tenantSlug) return [];
        const relativePath = currentPath.replace(`/${tenantSlug}/`, "").replace(`/${tenantSlug}`, "");
        return relativePath.split("/").filter(Boolean);
    }, [currentPath, tenantSlug]);

    const [routesReady, setRoutesReady] = useState(false);

    useEffect(() => {
        const POLL_INTERVAL_MS = 50;
        const POLL_TIMEOUT_MS = 5000;

        const checkLazyResolved = () => {
            const routes = routerUtils.flatRoutes as unknown as PaldockRoute[];
            const unresolved = routes.filter((route) => {
                const parent = route.options?.staticData?.sideMenuParent;
                return parent === "lazy";
            });
            if (unresolved.length > 0) {
                console.debug("[DynamicSideNavigation] Unresolved lazy routes:", unresolved.map((r) => r.id));
            }
            return unresolved.length === 0;
        };

        if (checkLazyResolved()) {
            setRoutesReady(true);
            return;
        }

        const startTime = Date.now();
        const interval = setInterval(() => {
            if (checkLazyResolved()) {
                clearInterval(interval);
                setRoutesReady(true);
            } else if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
                console.warn("[DynamicSideNavigation] Timed out waiting for lazy routes to resolve. Rendering menu anyway.");
                clearInterval(interval);
                setRoutesReady(true);
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [routerUtils.flatRoutes]);

    useEffect(() => {
        if (tenantSlug && pathSegments.length > 0) {
            const breadcrumbKeys = pathSegments.map((_, index) => {
                const linkChain = pathSegments.slice(0, index + 1).join("/");
                return `/${linkChain}`;
            });
            setOpenKeys(breadcrumbKeys);
        }
    }, [pathSegments, tenantSlug]);

    const onOpenChange = (keys: string[]) => setOpenKeys(keys);
    const handleLogout = () => userUtils.logoutUser();

    const routes = routerUtils.flatRoutes as unknown as PaldockRoute[];

    const routeType = tenantSlug ? RouteType.Tenant : RouteType.Account;

    const visibleRoutes = routes.filter((route) => {
        const sd = route.options?.staticData;
        if (!sd?.showInSideMenu) return false;
        if (sd?.type !== routeType) return false;
        return true;
    });

    const allowedRoutes = visibleRoutes.filter((route) => {
        const sd = route.options?.staticData;
        if (!sd) return false;
        return isRouteAccessible(sd, permissionUtils.hasAtLeastOnePermission);
    });

    const menuItems: MenuItem[] = buildMenuItems(allowedRoutes);

    const commonMenuItems: MenuItem[] = [
        {
            key: "divider-bottom",
            type: "divider",
            className: "bg-gray-100",
        },
        {
            key: "logout",
            icon: <LogoutOutlined />,
            label: tC("logout"),
            onClick: handleLogout,
        },
        {
            key: "divider-pin",
            type: "divider",
            className: "bg-gray-100",
        },
        {
            key: "pin",
            icon: <PushpinOutlined />,
            label: tC("pin"),
            onClick: toggleMenuCollapse,
        },
    ];

    const allMenuItems = [...menuItems, ...commonMenuItems];

    const selectedKeys = tenantSlug
        ? pathSegments.length > 0
            ? pathSegments.map((_, index) => {
                  return `/${pathSegments.slice(0, index + 1).join("/")}`;
              })
            : ["/"]
        : [currentPath];

    return (
        <nav>
            {routesReady ? (
                <Menu
                    theme="light"
                    mode="inline"
                    className="[&_.ant-menu-item-selected]:!bg-[#008080] [&_.ant-menu-item-selected]:!text-white [&_.ant-menu-item-selected_a]:!text-white"
                    selectedKeys={selectedKeys}
                    {...(tenantSlug ? { defaultSelectedKeys: ["1"], openKeys, onOpenChange } : {})}
                    items={allMenuItems}
                />
            ) : (
                <div className="flex flex-row py-4 justify-center text-center px-2">
                    <Spin />
                </div>
            )}
        </nav>
    );

    function buildMenuItems(routes: PaldockRoute[]) {
        const parentRoutes = routes
            .filter((route) => {
                const parent = route.options.staticData?.sideMenuParent;
                return parent === null || parent === undefined;
            })
            .sort((a, b) => {
                const aOrder = a.options.staticData?.sort ?? Number.MAX_SAFE_INTEGER;
                const bOrder = b.options.staticData?.sort ?? Number.MAX_SAFE_INTEGER;
                return aOrder - bOrder;
            });

        const items: MenuItem[] = [];
        let lastGroup: number | undefined;

        parentRoutes.forEach((route) => {
            const sd = route.options.staticData!;
            const link = resolvePath(route.to);

            if (lastGroup !== undefined && sd.sideMenuGroup !== undefined && sd.sideMenuGroup !== lastGroup) {
                items.push({
                    key: `divider-group-${lastGroup}-${sd.sideMenuGroup}`,
                    type: "divider",
                    className: "bg-gray-100",
                });
            }
            if (sd.sideMenuGroup !== undefined) {
                lastGroup = sd.sideMenuGroup;
            }

            const menuItem: MenuItem = {
                key: link,
                icon: sd.icon ?? null,
            };

            const childrenRoutes = routes
                .filter(
                    (childRoute) =>
                        childRoute.options.staticData?.sideMenuParent !== "lazy" &&
                        (childRoute.options.staticData?.sideMenuParent as { id: string } | null)?.id == route.id
                )
                .sort((a, b) => {
                    const aOrder = a.options.staticData?.sort ?? Number.MAX_SAFE_INTEGER;
                    const bOrder = b.options.staticData?.sort ?? Number.MAX_SAFE_INTEGER;
                    return aOrder - bOrder;
                });

            if (childrenRoutes.length > 0) {
                menuItem.label = tRaw(sd.label);
                menuItem.children = childrenRoutes.map((childRoute) => {
                    const sdChild = childRoute.options.staticData!;
                    const childLink = resolvePath(childRoute.to);
                    return {
                        key: childLink,
                        label: renderLink(childLink, tRaw(sdChild.label)),
                        icon: sdChild.icon ?? null,
                    };
                });
            } else {
                menuItem.label = renderLink(link, tRaw(sd.label));
            }

            items.push(menuItem);
        });

        return items;
    }

    function resolvePath(routeTo: string): string {
        const path = tenantSlug ? removeTenantSlugParam(routeTo) : routeTo;
        return removeTrailingSlash(path);
    }

    function renderLink(to: string, label: string): ReactNode {
        return tenantSlug ? <TenantLink to={to}>{label}</TenantLink> : <Link to={to}>{label}</Link>;
    }
};

function removeTrailingSlash(path: string): string {
    if (path.length > 1 && path.endsWith("/")) {
        return path.slice(0, -1);
    }
    return path;
}
